import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bAccessory, B2bContractItemAccessory } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-accessories',
  templateUrl: './b2b-accessories.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-accessories.component.css']
})
export class B2bAccessoriesComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  accessories: B2bAccessory[] = [];
  contractItems: Array<{ id: number; label: string }> = [];
  itemAccessories: B2bContractItemAccessory[] = [];
  selectedItemId: number | null = null;
  isLoading = false;
  isLoadingLinks = false;
  submitting = false;
  linking = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  form!: FormGroup;
  linkForm!: FormGroup;

  columns = [
    { titulo: 'Código', label: 'code' },
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Valor unit.', label: 'unitValue', formatter: (v: any) => this.formatMoney(v), sensitive: true },
    { titulo: 'Ativo', label: 'isActive', formatter: (v: any) => (v ? 'Sim' : 'Não') },
    { titulo: 'Descrição', label: 'description' }
  ];

  linkColumns = [
    { titulo: 'Acessório', label: 'accessoryName' },
    { titulo: 'Código', label: 'accessoryCode' },
    { titulo: 'Qtd', label: 'quantity' },
    { titulo: 'Obs.', label: 'notes' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar acessório';
    return this.editingId ? 'Editar acessório' : 'Novo acessório';
  }

  linkActions: TableAction[] = [
    { label: 'Desvincular', icon: 'fas fa-unlink', action: 'unlink', class: 'btn-delete' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      code: [''],
      name: ['', Validators.required],
      description: [''],
      unitValue: [null],
      isActive: [true]
    });

    this.linkForm = this.fb.group({
      b2bAccessoryId: [null, Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      notes: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadAccessories();
        this.loadContractItems();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadAccessories(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bAccessories(this.contractId).subscribe({
      next: (list) => {
        this.accessories = (list || []).map(x => this.normalizeAccessory(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  loadContractItems(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractItems(this.contractId, 1, 100).subscribe({
      next: (res) => {
        const raw = res?.data || res?.Data || (Array.isArray(res) ? res : []);
        this.contractItems = (raw || []).map((it: any) => {
          const id = it.id ?? it.Id;
          const num = it.itemNumber ?? it.ItemNumber ?? id;
          return { id, label: `Pedido ${num}` };
        }).filter((x: any) => x.id != null);
      },
      error: () => { this.contractItems = []; }
    });
  }

  onItemChange(): void {
    this.itemAccessories = [];
    if (this.selectedItemId) this.loadItemAccessories();
  }

  loadItemAccessories(): void {
    if (!this.contractId || !this.selectedItemId) return;
    this.isLoadingLinks = true;
    this.contractsService.getB2bItemAccessories(this.contractId, this.selectedItemId).subscribe({
      next: (list) => {
        this.itemAccessories = (list || []).map(x => this.normalizeLink(x));
        this.isLoadingLinks = false;
      },
      error: () => { this.isLoadingLinks = false; }
    });
  }

  openCreate(): void {
    this.viewMode = false;
    this.editingId = null;
    this.form.enable();
    this.form.reset({ code: '', name: '', description: '', unitValue: null, isActive: true });
    this.showModal = true;
  }

  openView(item: B2bAccessory): void {
    this.patchForm(item);
    this.viewMode = true;
    this.form.disable();
    this.showModal = true;
  }

  openEdit(item: B2bAccessory): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bAccessory): void {
    this.editingId = item.id ?? null;
    this.form.patchValue({
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
      unitValue: item.unitValue ?? null,
      isActive: item.isActive !== false
    });
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload: B2bAccessory = {
      b2bContractId: this.contractId,
      code: raw.code || null,
      name: raw.name,
      description: raw.description || null,
      unitValue: raw.unitValue != null && raw.unitValue !== '' ? Number(raw.unitValue) : null,
      isActive: !!raw.isActive
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bAccessory(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bAccessory(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Acessório atualizado.' : 'Acessório criado.');
        this.closeModal();
        this.loadAccessories();
      },
      error: () => { this.submitting = false; }
    });
  }

  linkAccessory(): void {
    if (!this.contractId || !this.selectedItemId || this.linkForm.invalid) {
      this.linkForm.markAllAsTouched();
      return;
    }
    const raw = this.linkForm.getRawValue();
    const payload: B2bContractItemAccessory = {
      b2bContractItemId: this.selectedItemId,
      b2bAccessoryId: Number(raw.b2bAccessoryId),
      quantity: Number(raw.quantity) || 1,
      notes: raw.notes || null
    };

    this.linking = true;
    this.contractsService.linkB2bItemAccessory(this.contractId, this.selectedItemId, payload).subscribe({
      next: () => {
        this.linking = false;
        this.errorService.showSuccessMessage('Acessório vinculado ao pedido.');
        this.linkForm.patchValue({ b2bAccessoryId: null, quantity: 1, notes: '' });
        this.loadItemAccessories();
      },
      error: () => { this.linking = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bAccessory }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteAccessory(event.item);
  }

  onLinkActionClick(event: { action: string; item: B2bContractItemAccessory }): void {
    if (event.action === 'unlink') this.unlinkAccessory(event.item);
  }

  deleteAccessory(item: B2bAccessory): void {
    if (!this.contractId || !item.id) return;
    this.confirmationService.confirmDelete(item.name || 'este acessório', 'o acessório').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bAccessory(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Acessório excluído.');
            this.loadAccessories();
          }
        });
      }
    });
  }

  unlinkAccessory(item: B2bContractItemAccessory): void {
    if (!this.contractId || !this.selectedItemId || !item.id) return;
    this.confirmationService.confirmDelete(item.accessoryName || 'este vínculo', 'o vínculo').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.unlinkB2bItemAccessory(this.contractId!, this.selectedItemId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Vínculo removido.');
            this.loadItemAccessories();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-acessorios-${this.contractNumber || this.contractId}`,
      this.columns,
      this.accessories,
      'Catalogo'
    );
  }

  exportLinksExcel(): void {
    exportB2bTableToExcel(
      `b2b-acessorios-pedido-${this.contractNumber || this.contractId}`,
      this.linkColumns,
      this.itemAccessories,
      'Vinculos'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalizeAccessory(dto: any): B2bAccessory {
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      code: dto.code ?? dto.Code ?? null,
      name: dto.name ?? dto.Name ?? '',
      description: dto.description ?? dto.Description ?? null,
      unitValue: dto.unitValue ?? dto.UnitValue ?? null,
      isActive: dto.isActive ?? dto.IsActive ?? true
    };
  }

  private normalizeLink(dto: any): B2bContractItemAccessory {
    const accessory = dto.accessory ?? dto.Accessory ?? {};
    return {
      id: dto.id ?? dto.Id,
      b2bContractItemId: dto.b2bContractItemId ?? dto.b2BContractItemId ?? dto.B2BContractItemId ?? this.selectedItemId!,
      b2bAccessoryId: dto.b2bAccessoryId ?? dto.b2BAccessoryId ?? dto.B2BAccessoryId,
      quantity: dto.quantity ?? dto.Quantity ?? 1,
      notes: dto.notes ?? dto.Notes ?? null,
      accessoryName: dto.accessoryName ?? dto.AccessoryName ?? accessory.name ?? accessory.Name ?? '—',
      accessoryCode: dto.accessoryCode ?? dto.AccessoryCode ?? accessory.code ?? accessory.Code ?? null
    };
  }

  formatMoney(value: any): string {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}
